import type { PaymentOptionsRecord } from '@navio/nsdk';
import {
  organizationBookingDrivers,
  toOrganizationBookingPeople,
  type OrganizationBookingPerson
} from './organizationBookingAssignee';
import { organizationCarInvoiceReferenceRule, type OrganizationCarBooking } from './organizationCarBooking';
import {
  checkoutInvoiceRecipientName,
  checkoutInvoiceToUserGroupId,
  deriveNonMemberOwnerNotice,
  deriveStationaryBookingParty,
  type MemberOrganization
} from './stationaryCheckout';

/**
 * Everything on a booking summary that depends on which organization the
 * booking is on: the party it is created on, how an invoice is named and
 * sent, and the drivers it can be assigned to. Home delivery and the service
 * center share this, so a rule changes in one place.
 */
export function deriveOrganizationCheckout(args: {
  isLoggedIn: boolean;
  memberUserGroupId: number | null;
  memberOrganization: MemberOrganization | null;
  organizationCar: OrganizationCarBooking | null;
  paymentOptions: PaymentOptionsRecord | undefined;
  isInvoice: boolean;
  showPaymentChoice: boolean;
  /** Drivers of the customer's own organization, from the car defaults. */
  memberDrivers: OrganizationBookingPerson[];
  /** Names an invoice falls back to when neither payer nor organization names one. */
  fallbackNames: Array<string | null | undefined>;
}) {
  const { isLoggedIn, organizationCar, paymentOptions, isInvoice, showPaymentChoice } = args;
  const bookingParty = deriveStationaryBookingParty({
    memberUserGroupId: args.memberUserGroupId,
    memberOrganization: args.memberOrganization,
    organizationCar,
    paymentOptions,
    bookOnOrganizationCar: isInvoice
  });

  return {
    bookingParty,
    invoiceTo: bookingParty.invoiceTo,
    invoiceRecipientName: checkoutInvoiceRecipientName({
      party: bookingParty,
      organizationCar,
      fallbacks: args.fallbackNames
    }),
    invoiceToUserGroupId: checkoutInvoiceToUserGroupId(isInvoice, bookingParty.invoiceTo),
    invoiceReferenceRule: organizationCarInvoiceReferenceRule(bookingParty, isInvoice),
    nonMemberOwnerNotice: deriveNonMemberOwnerNotice({
      isLoggedIn,
      bookingParty,
      paymentOptions,
      isInvoice,
      showPaymentChoice
    }),
    organizationDrivers: organizationBookingDrivers({
      party: bookingParty,
      memberDrivers: args.memberDrivers,
      nonMemberDrivers: toOrganizationBookingPeople(paymentOptions?.nonMemberBookableOrganization?.drivers ?? [])
    })
  };
}
